/**
 * Demande de rendez-vous par message (lead APPOINTMENT_REQUEST, POST /api/leads)
 * pour les fiches CLIENTES qu'aucun patient ne peut joindre.
 *
 * Constat du 21/10/2026 (bilan, anomalies A5 et A12) : 5 fiches Gold avec compte
 * (dont Dr Al Timimi, réservé 2027 et 1er des cardiologues de Casablanca) n'ont
 * ni téléphone, ni WhatsApp, ni RDV en ligne. Le bouton « Demander un RDV »
 * menait à /rdv/{slug}, qui disait seulement « n'a pas communiqué de numéro »
 * puis renvoyait vers la liste des confrères : une impasse, sur une fiche
 * mise en tête des listes.
 *
 * Mécanisme EXISTANT réutilisé, rien de nouveau côté serveur : POST /api/leads
 * enregistre le Lead rattaché à la fiche (physicianId), visible dans
 * /espace-pro/leads du médecin et /admin/leads ; alerte e-mail à l'équipe
 * (ADMIN_ALERT_EMAIL, contact@sahha.ma par défaut) ; e-mail au médecin si
 * Physician.email est renseigné ; notification in-app au compte rattaché.
 *
 * Module sans dépendance serveur (importé par un composant client et par les
 * tests node). Le contrat du corps suit `leadSchema` (src/app/api/leads/route.ts,
 * non exportable) : tests/node/appointment-request.test.ts fait passer chaque
 * corps construit ici dans le VRAI handler POST.
 */
import { isOnlineBookingActive, type PhysicianTier } from "@/lib/data/physician-tier";
import { callablePhone } from "@/lib/data/physician-phone";
import { isValidMarPhone, whatsappHref } from "@/lib/labs/format";
import { compactPhone, isLeadEmail, LEAD_LIMITS, toLeadLocale, type LeadLocale } from "@/components/mre/lead-request";

/** Préfixe de Lead.source : « rdv-request:{surface} » (rdv-page, fiche…). */
export const APPOINTMENT_REQUEST_SOURCE = "rdv-request";

export type AppointmentRequestTarget = {
  /** Tier RÉEL (pas effectiveTier : un placement offert n'est pas un client). */
  tier: PhysicianTier;
  /** Fiche rattachée à un compte (userId) ou revendiquée (claimedAt). */
  isClaimed?: boolean;
  onlineBookingEnabled?: boolean;
  phone?: string | null;
  /** WhatsApp affichable (sur /rdv : seulement celui d'une fiche rattachée à un compte). */
  whatsapp?: string | null;
  isCenter?: boolean;
  /** Statut base (SUSPENDED, CLOSED…), quand il est connu. */
  status?: string | null;
};

/**
 * Vrai quand la fiche est cliente (compte, revendication, Gold ou Premium —
 * même règle qu'isRegisteredOnSahha) mais qu'un patient n'a AUCUN moyen de la
 * joindre : pas de RDV en ligne actif, pas de numéro appelable, pas de WhatsApp.
 * Les fiches non clientes (VERIFIED sans compte, non revendiquées) ne reçoivent
 * pas de demande : personne ne la lirait, elles gardent les praticiens
 * similaires joignables de /rdv/{slug}.
 */
export function acceptsAppointmentRequest(p: AppointmentRequestTarget): boolean {
  if (p.isCenter) return false;
  if (p.status === "SUSPENDED" || p.status === "CLOSED") return false;
  const isClient = p.isClaimed === true || p.tier === "gold" || p.tier === "premium";
  if (!isClient) return false;
  if (isOnlineBookingActive({ tier: p.tier, onlineBookingEnabled: p.onlineBookingEnabled })) return false;
  if (callablePhone(p.phone) !== null) return false;
  if (whatsappHref(p.whatsapp) !== null) return false;
  return true;
}

export type AppointmentRequestFields = {
  firstName: string;
  lastName: string;
  phone: string;
  email: string;
  message: string;
  consent: boolean;
};

export type AppointmentRequestField = keyof AppointmentRequestFields;
export type AppointmentRequestError = "required" | "phoneInvalid" | "emailInvalid" | "consentRequired";

/** Ordre d'affichage (focus sur le premier champ en erreur). */
export const APPOINTMENT_REQUEST_FIELD_ORDER: readonly AppointmentRequestField[] = [
  "firstName",
  "lastName",
  "phone",
  "email",
  "message",
  "consent",
];

/** Validation locale alignée sur le serveur (phoneMar, z.string().email()). */
export function validateAppointmentRequest(
  f: AppointmentRequestFields,
): Partial<Record<AppointmentRequestField, AppointmentRequestError>> {
  const errs: Partial<Record<AppointmentRequestField, AppointmentRequestError>> = {};
  if (!f.firstName.trim()) errs.firstName = "required";
  const phone = compactPhone(f.phone);
  if (!phone) errs.phone = "required";
  else if (!isValidMarPhone(phone)) errs.phone = "phoneInvalid";
  const email = f.email.trim();
  if (email && !isLeadEmail(email)) errs.email = "emailInvalid";
  if (!f.consent) errs.consent = "consentRequired";
  return errs;
}

export type AppointmentRequestBody = {
  type: "APPOINTMENT_REQUEST";
  physicianSlug: string;
  phone: string;
  firstName?: string;
  lastName?: string;
  email?: string;
  message?: string;
  source: string;
  preferredLanguage: LeadLocale;
};

/** Vide → absent : le schéma refuse "" sur les champs min(1) et email. */
function clean(value: string, max: number): string | undefined {
  const v = value.trim();
  return v ? v.slice(0, max) : undefined;
}

/**
 * Corps POST /api/leads. Le message porte une ligne d'en-tête : l'alerte e-mail
 * (leadAdminAlertTemplate) n'affiche pas le type de demande en clair.
 * preferredLanguage = langue de la page : le cabinet sait dans quelle langue rappeler.
 */
export function buildAppointmentRequestLead(
  f: AppointmentRequestFields,
  ctx: { physicianSlug: string; locale: string; surface: string },
): AppointmentRequestBody {
  const header = "[Demande de RDV par message — fiche sans téléphone ni RDV en ligne]";
  const text = clean(f.message, Math.max(0, LEAD_LIMITS.message - header.length - 1));
  return {
    type: "APPOINTMENT_REQUEST",
    physicianSlug: ctx.physicianSlug,
    firstName: clean(f.firstName, LEAD_LIMITS.name),
    lastName: clean(f.lastName, LEAD_LIMITS.name),
    phone: compactPhone(f.phone),
    email: clean(f.email, 254),
    message: (text ? `${header}\n${text}` : header).slice(0, LEAD_LIMITS.message),
    source: `${APPOINTMENT_REQUEST_SOURCE}:${ctx.surface}`.slice(0, LEAD_LIMITS.source),
    preferredLanguage: toLeadLocale(ctx.locale),
  };
}
